import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { AcceptanceStatus, dateString, separateItemsByDate, UtilsService } from 'projects/common/src/public-api';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, mergeMap, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { ModalsService } from '../../../../../common/src/lib/services/modals.service';
import { ActivatedRoute, Router } from '@angular/router';
import { UsersService } from '../../services/users.service';
import { NotesService } from '../../services/notes.service';
import { BusinessService } from '../../services/business.service';
import { TimeRange } from 'projects/common/src/lib/models/time-range.model';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { SCREEN_BREAKPOINTS } from 'projects/common/src/lib/directives/SCREEN_BREAKPOINTS';
import { NavigationService } from '../../services/navigation.service';
import { Assessment } from "../../models/assessment.model";
import { UserProfile } from "../../../../../common/src/lib/models/user-profile.model";
import { Job } from '../../models/jobs.model';
import { ViewAsService } from "../../services/view-as.service";
import { Lead } from 'projects/common/src/lib/models/lead.model';

const tabs = ['Info', 'Notes'];

type DocType = 'Lead' | 'Assessment' | 'Job';

interface IDocument {
  id: number;
  workflowId: number;
  docType: DocType;
  jobType: string;
  ranges: TimeRange[];
}

@Component({
  template: '',
})
export abstract class EventInfoComponent<T extends IDocument> implements OnInit, AfterViewInit, OnDestroy {
  abstract ownerOrLastAssignedBy$: Observable<UserProfile>;
  abstract assignees$: Observable<{user: UserProfile, acceptanceStatus?: AcceptanceStatus}[] | null>;
  abstract deleteDocument(id: number, workflowId: number): Promise<void>;
  abstract editDocument(document: T): Promise<void>;
  abstract getAssignees(document: T): number[];
  abstract setActiveTab(tab: string): Promise<void>;

  @ViewChild('jobTypeInput') jobTypeInput!: ElementRef;
  @ViewChild('contentDetails') contentDetails!: ElementRef;

  readonly timeFormat = 'hh:mm a';
  showBottomButtons = true;
  contentScrollThreshold = 0;
  jobType?: string;
  jobTypeFocused = false;
  jobTypeControl = new FormControl<string>('', { nonNullable: true });
  documentId = +this.route.snapshot.params['documentId'];
  workflowId = +this.route.snapshot.params['workflowId'];

  docSubject = new BehaviorSubject<Observable<T | null>>(of(null).pipe(filter(() => false)));
  tabsSubject = new BehaviorSubject<string[]>(tabs);
  selectedTabSubject = new BehaviorSubject<string>(tabs[0]);
  selectedSideTabSubject = new BehaviorSubject<string>(tabs.slice(1)[0]);
  allTabs$ = this.tabsSubject.asObservable();
  sideTabs$ = this.allTabs$.pipe(map(tabs => tabs.slice(1)));

  currentUser$ = this.usersService.currentUser$;
  selectedBusiness$ = this.businessService.selectedBusiness$;

  tabletScreen$ = this.utilsService.onScreenBreakpointChange('sm');
  largeScreen$ = this.utilsService.onScreenBreakpointChange('lg');
  
  tabs$ = combineLatest([
    this.largeScreen$,
    this.allTabs$,
    this.sideTabs$,
  ]).pipe(
    map(([largeScreen, tabs, sideTabs]) => (largeScreen ? sideTabs : tabs) as string[]),
    shareReplay({bufferSize: 1, refCount: true})
  );

  selectedTab$ = combineLatest([
    this.largeScreen$,
    this.selectedTabSubject,
    this.selectedSideTabSubject
  ]).pipe(
    map(([largeScreen, mainTab, sideTab]) => largeScreen ? sideTab : mainTab),
    shareReplay({bufferSize: 1, refCount: true})
  );

  showInfo$ = combineLatest([this.largeScreen$, this.selectedTab$]).pipe(
    map(([largeScreen, selectedTab]) => largeScreen || selectedTab === 'Info'),
    shareReplay({bufferSize: 1, refCount: true})
  );

  showNotes$ = this.selectedTab$.pipe(
    map((selectedTab) => selectedTab === 'Notes'),
    shareReplay({bufferSize: 1, refCount: true})
  );

  document$ = this.docSubject.pipe(
    switchMap(doc => doc),
    tap(doc => {
      if (!doc) {
        this.modalsService.close();
        this.router.navigate(['/lobby']);
        return;
      }
      if (this.jobTypeControl.value !== doc.jobType) {
        if (this.jobTypeFocused) {
          this.lastIgnoredJobType = doc.jobType;
        } else {
          this.jobTypeControl.setValue(doc.jobType ?? '', {emitEvent: false});
        }
      }
    }),
    shareReplay({bufferSize: 1, refCount: true})
  );

  dialogTitle$ = this.document$.pipe(
    map(doc => doc ? `${doc.docType} info` : '')
  );

  documentByDate$ = this.document$.pipe(
    map((assessment) => assessment?.ranges ?? null),
    filter(ranges => !!ranges),
    mergeMap((ranges) => separateItemsByDate<TimeRange>((range) => dateString(range.startTime, range.endTime))(of(ranges!))),
    map((items) => {
      const itemsArray: [string, TimeRange[]][] = [];
      for (const date in items) {
        if (items[date].length !== 0) {
          itemsArray.push([date, items[date].sort((a, b) => a.startTime > b.startTime ? 1 : -1)]);
        }
      }
      return itemsArray.sort((a, b) => a[1][0].startTime > b[1][0].startTime ? 1 : -1);
    })
  );

  readonly$ = this.viewAsService.readonly$;


  private jobTypeUpdateSub?: Subscription;
  private jobTypeSub?: Subscription;
  private paramMapSub?: Subscription;
  private scrollSubscription?: Subscription;
  private lastIgnoredJobType?: string;
  private blockContentScrollHandle = false;
  private bottomScrollOffset = this.utilsService.isIOS() ? 100 : 1;
  private resizeObserver: ResizeObserver | null = null;

  constructor(
    public businessService: BusinessService,
    protected modalsService: ModalsService,
    private utilsService: UtilsService,
    protected router: Router,
    private route: ActivatedRoute,
    protected usersService: UsersService,
    public notesService: NotesService,
    public scrollDispatcher: ScrollDispatcher,
    protected viewAsService: ViewAsService,
    private navigationService: NavigationService,
    private zone: NgZone,
    private window: Window,
  ) {}

  async ngOnInit() {
    await this.navigationService.setStateQueryParam();
    this.initJobType();
    this.paramMapSub = this.route.paramMap.subscribe(params => {
      const documentId = params.get('documentId');
      if (documentId && this.documentId !== +documentId)
        location.reload();
    });
  }

  ngAfterViewInit() {
    this.initResizeObserver();
    this.jobTypeInput?.nativeElement?.blur();

    this.scrollSubscription = this.scrollDispatcher.scrolled(100).subscribe(
      (scrollSource) => {
        const contentElement = this.contentDetails.nativeElement;
        if (!scrollSource
          || scrollSource.getElementRef().nativeElement !== contentElement
          || contentElement.scrollHeight - contentElement.clientHeight <= this.contentScrollThreshold
        ) {
          return;
        }

        if (this.blockContentScrollHandle) {
          this.blockContentScrollHandle = false;
          return;
        }
        this.zone.run(() => {
          this.showBottomButtons = scrollSource.measureScrollOffset('bottom') <= this.bottomScrollOffset;
        });
      }
    );
  }

  async initJobType() {
    this.jobTypeSub = this.jobTypeControl.valueChanges.pipe(
      distinctUntilChanged(),
      debounceTime(1000)
    )
    .subscribe(value => {
        if(value.trim() === '') {
          this.jobTypeControl.setValue(this.jobType ?? '', { emitEvent: false });
        } else {
          this.notesService.updateJobType(this.workflowId!, value.trim());
          this.jobType = value;
        }
      }
    );

    this.jobTypeUpdateSub = this.notesService.jobTypeObservable(this.workflowId)
      .subscribe(jobType => {
        if (jobType !== this.jobType?.trim())
          this.jobTypeControl.setValue(jobType!, { emitEvent: !this.jobType });
      }
    );
  }

  onJobTypeUnfocus() {
    this.jobTypeFocused = false;
    if(this.lastIgnoredJobType) {
      this.jobTypeControl.setValue(this.lastIgnoredJobType, { emitEvent: false });
      this.lastIgnoredJobType = undefined;
    }
  }

  ngOnDestroy() {
    this.scrollSubscription?.unsubscribe();
    this.jobTypeUpdateSub?.unsubscribe();
    this.jobTypeSub?.unsubscribe;
    this.paramMapSub?.unsubscribe();
    this.resizeObserver?.disconnect();
    this.disconnectResizeObserver();
  }

  isLead(document: T): document is T & Lead {
    return document.docType === 'Lead';
  }

  isAssessment(document: T): document is T & Assessment {
    return document.docType === 'Assessment';
  }

  isJob(document: T): document is T & Job {
    return document.docType === 'Job';
  }
   
  async initResizeObserver() {
    const showSummary = await this.showInfo$.pipe(take(1)).toPromise();
    if(!showSummary || !this.contentDetails)
      return;
    const contentElement = this.contentDetails.nativeElement;
    this.resizeObserver = new ResizeObserver(() => {
      if (this.window.innerWidth >= SCREEN_BREAKPOINTS['sm']) {
        this.showBottomButtons = false;
        return;
      }

      const footer = document.querySelector('.footer');
      const modifyButtons = footer?.querySelector('.event-modify-buttons');
      if (footer && modifyButtons) {
        this.contentScrollThreshold = parseInt(getComputedStyle(footer).gap) + modifyButtons.getBoundingClientRect().height;
      }

      const bottomScrollOffset = contentElement.scrollHeight - contentElement.clientHeight - contentElement.scrollTop;
      if (contentElement.scrollHeight - contentElement.clientHeight <= this.contentScrollThreshold) {
        this.showBottomButtons = true;
        return;
      }

      if (bottomScrollOffset > this.contentScrollThreshold * 2) {
        this.showBottomButtons = false;
      }
    });

    this.resizeObserver.observe(contentElement);
  }

  disconnectResizeObserver() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  async close() {
    this.navigationService.goBack();
  }

  get backButtonCaption() {
    return this.navigationService.fromReports ? 'Back' : 'Close';
  }

  get isMobile() {
    return this.utilsService.isMobile();
  }

  formatLastName(lastName: string): string {
    if (lastName && lastName.length > 0) {
      return lastName[0].toUpperCase() + '.';
    }
    return '';
  }

}